import React, { useState, useEffect } from 'react'
import { Row, Col, Input, Upload, Button, message, Table, Spin } from 'antd'
import { UploadOutlined, SendOutlined, FileWordOutlined, SearchOutlined } from '@ant-design/icons'
import Layout from '../Home'
import httpEverHealthCare from '../../services/httpServiceHealthCare'
import useHNConverter from '../../hooks/DataConverter/useHNConverter/useHNConverter'

const Airdoc = (props) => {

  const [hnPart1, setHnPart1] = useState('')
  const [hnPart2, setHnPart2] = useState('')
  const [fileList, setFileList] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableData, setTableData] = useState([])
  const [tableLoading, setTableLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  const handleHnPart1Change = (e) => {
    const value = e.target.value.replace(/\D/g, '')
    setHnPart1(value)
  }

  const handleHnPart2Change = (e) => {
    const value = e.target.value.replace(/\D/g, '')
    setHnPart2(value)
  }

  const handleUpload = (info) => {
    const { file } = info
    const isPdf = file.type === 'application/pdf'
    const isImage = file.type.startsWith('image/')
    if (!isPdf && !isImage) {
      message.error('กรุณาอัพโหลดไฟล์ PDF หรือรูปภาพเท่านั้น')
      setFileList([])
      return
    }

    const newFileList = [...fileList]
    if (info.file.status === 'removed') {
      const index = newFileList.findIndex((item) => item.uid === file.uid)
      if (index !== -1) {
        newFileList.splice(index, 1)
      }
      setFileList(newFileList)
      return
    }

    const fileIndex = newFileList.findIndex((item) => item.uid === file.uid)
    if (fileIndex === -1) {
      newFileList.push(file)
    } else {
      newFileList[fileIndex] = file
    }
    setFileList(newFileList)
  }

  const beforeUpload = (file) => {
    const isPdf = file.type === 'application/pdf'
    const isImage = file.type.startsWith('image/')
    if (!isPdf && !isImage) {
      message.error('กรุณาอัพโหลดไฟล์ PDF หรือรูปภาพเท่านั้น!')
      return false
    }
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      message.error('ไฟล์ต้องมีขนาดไม่เกิน 10MB!')
      return false
    }
    return false
  }

  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok')
    }, 0)
  }

  const fetchTableData = async (page = 1, limit = 10, search = '') => {
    setTableLoading(true)
    try {
      const params = {
        page,
        limit,
      }

      // เพิ่ม search parameter ถ้ามีค่า
      if (search && search.trim()) {
        params.search = search.trim()
      }

      const response = await httpEverHealthCare.get('/WorklistOcrs/getAll', {
        params,
      })

      if (response.data) {
        if (response.data.data && Array.isArray(response.data.data) && response.data.pagination) {
          setTableData(response.data.data)
          setPagination((prev) => ({
            ...prev,
            current: response.data.pagination.page || page,
            pageSize: response.data.pagination.limit || limit,
            total: response.data.pagination.total || 0,
          }))
        }
        else if (Array.isArray(response.data)) {
          setTableData(response.data)
          setPagination((prev) => ({
            ...prev,
            current: page,
            total: response.data.length,
          }))
        }
        else if (response.data.data && Array.isArray(response.data.data)) {
          setTableData(response.data.data)
          setPagination((prev) => ({
            ...prev,
            current: page,
            total: response.data.total || response.data.count || 0,
          }))
        }
        else if (response.data.results && Array.isArray(response.data.results)) {
          setTableData(response.data.results)
          setPagination((prev) => ({
            ...prev,
            current: page,
            total: response.data.total || response.data.count || 0,
          }))
        }
      }
    } catch (error) {
      console.error('Error fetching table data:', error)
      message.error('เกิดข้อผิดพลาดในการดึงข้อมูล')
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    fetchTableData(1, 10, searchTerm)
  }, [])

  const handleSearch = () => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }))

    // ตรวจสอบว่ามี "/" ใน search term หรือไม่ (น่าจะเป็น HN format)
    let searchValue = searchTerm.trim()
    if (searchValue.includes('/')) {
      try {
        const { revertHN } = useHNConverter(searchValue)
        searchValue = revertHN()
      } catch (error) {
        console.error('Error converting HN:', error)
        // ถ้าแปลงไม่ได้ ให้ใช้ค่าเดิม
      }
    }

    fetchTableData(1, pagination.pageSize, searchValue)
  }

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleExportToWord = (record) => {
    try {
      const firstName = record?.appUser?.userInformation?.firstname || ''
      const lastName = record?.appUser?.userInformation?.lastname || ''
      const fullName = record?.name || `${firstName} ${lastName}`.trim() || '-'

      const hn = record?.hn || '-'
      const result = record?.result || 'รอผล OCR'
      const createdAt = record?.createdAt
        ? new Date(record.createdAt).toLocaleString('th-TH')
        : '-'

      // สร้าง HTML content สำหรับ Word document
      const htmlContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8">
          <title>ข้อมูล OCR</title>
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>90</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
            @page {
              size: A4;
              margin: 2.5cm 2cm 2cm 2cm;
            }
            body {
              font-family: 'TH Sarabun New', 'Sarabun', 'Angsana New', 'Angsana', sans-serif;
              font-size: 16pt;
              line-height: 1.6;
            }
            p {
              margin: 10pt 0;
            }
            .label {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <p><span class="label">ชื่อนามสกุล:</span> ${fullName}</p>
          <p><span class="label">HN:</span> ${hn}</p>
          <p><span class="label">วันที่ตรวจ:</span> ${createdAt}</p>
          <br/>
          <p><span class="label">ผลที่ OCR:</span></p>
          <p>${result.replace(/\n/g, '<br/>')}</p>
        </body>
        </html>
      `

      // สร้าง Blob จาก HTML content
      const blob = new Blob(['\ufeff', htmlContent], {
        type: 'application/msword;charset=utf-8;',
      })

      // สร้าง link element และ trigger download
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = `OCR_${hn}_${record?.id || 'unknown'}.doc`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      message.success('ส่งออกไฟล์ Word สำเร็จ')
    } catch (error) {
      console.error('Error exporting to Word:', error)
      message.error('เกิดข้อผิดพลาดในการส่งออกไฟล์ Word')
    }
  }

  const handleSubmit = async () => {
    if (!hnPart1 || !hnPart2) {
      message.warning('กรุณากรอก HN ให้ครบถ้วน')
      return
    }

    if (fileList.length === 0) {
      message.warning('กรุณาอัพโหลดไฟล์ PDF หรือรูปภาพ')
      return
    }

    setLoading(true)

    try {
      const formData = new FormData()
      const hnFormatted = `${hnPart1}/${hnPart2}`
      const { revertHN } = useHNConverter(hnFormatted)
      const revertedHN = revertHN()

      formData.append('hn', revertedHN)

      fileList.forEach((file) => {
        const fileToAppend = file.originFileObj || file
        if (fileToAppend) {
          // ตรวจสอบว่าเป็น PDF หรือ image เพื่อใช้ field name ที่เหมาะสม
          const isPdf = fileToAppend.type === 'application/pdf'
          const fieldName = isPdf ? 'pdf' : 'image'
          formData.append(fieldName, fileToAppend)
        }
      })

      const response = await httpEverHealthCare.post('/WorklistOcrs/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      message.success('ส่งข้อมูลไป OCR สำเร็จ')

      setHnPart1('')
      setHnPart2('')
      setFileList([])

      fetchTableData(pagination.current, pagination.pageSize, searchTerm)
    } catch (error) {
      console.error('Error submitting to OCR:', error)
      message.error(error.response?.data?.error?.message || 'เกิดข้อผิดพลาดในการส่งข้อมูลไป OCR')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Layout
      {...props}
      render={(
        renderProps, //eslint-disable-line
      ) => (
        <div style={{ padding: '20px', width: '100%' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col>
              <label style={{ marginRight: '8px', fontWeight: 'bold' }}>HN:</label>
            </Col>
            <Col>
              <Input
                value={hnPart1}
                onChange={handleHnPart1Change}
                placeholder="ตัวเลข"
                style={{ width: '120px', textAlign: 'center' }}
                maxLength={10}
              />
            </Col>
            <Col>
              <span style={{ fontSize: '18px', fontWeight: 'bold', padding: '0 8px' }}>/</span>
            </Col>
            <Col>
              <Input
                value={hnPart2}
                onChange={handleHnPart2Change}
                placeholder="ตัวเลข"
                style={{ width: '120px', textAlign: 'center' }}
                maxLength={10}
              />
            </Col>
            <Col style={{ marginLeft: '24px' }}>
              <Upload
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,image/*"
                beforeUpload={beforeUpload}
                customRequest={customRequest}
                onChange={handleUpload}
                fileList={fileList}
                onRemove={(file) => {
                  const newFileList = fileList.filter((item) => item.uid !== file.uid)
                  setFileList(newFileList)
                }}
              >
                <Button icon={<UploadOutlined />}>อัปโหลดไฟล์ (PDF/รูปภาพ)</Button>
              </Upload>
            </Col>
            <Col style={{ marginLeft: '16px' }}>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSubmit}
                loading={loading}
                disabled={!hnPart1 || !hnPart2 || fileList.length === 0}
              >
                ส่งไป OCR
              </Button>
            </Col>
          </Row>

          {/* Search Section */}
          <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
            <Col>
              <Input
                placeholder="ค้นหาชื่อหรือ HN"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onPressEnter={handleSearchKeyPress}
                style={{ width: '300px' }}
                allowClear
              />
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                ค้นหา
              </Button>
            </Col>
          </Row>

          {/* Table Section */}
          <div style={{ marginTop: '32px' }}>
            <Spin spinning={tableLoading}>
              <Table
                columns={[
                  {
                    title: 'ID',
                    dataIndex: 'id',
                    key: 'id',
                    width: 80,
                  },
                  {
                    title: 'HN',
                    dataIndex: 'hn',
                    key: 'hn',
                    width: 150,
                    render: (text, record) => {
                      const { convertHN } = useHNConverter(text)
                      const convertedHN = convertHN()
                      return convertedHN
                    }
                  },
                  {
                    title: 'ชื่อ-นามสกุล',
                    key: 'name',
                    width: 200,
                    render: (text, record) => {
                      const firstName = record?.appUser?.userInformation?.firstname || ''
                      const lastName = record?.appUser?.userInformation?.lastname || ''
                      const fullName = record?.name || `${firstName} ${lastName}`.trim()
                      return fullName || '-'
                    },
                  },
                  {
                    title: 'ไฟล์ PDF',
                    dataIndex: 'linkFile',
                    key: 'linkFile',
                    width: 200,
                    render: (text, record) => {
                      if (record.linkFile) {
                        return (
                          <a href={record.linkFile} target="_blank" rel="noopener noreferrer">
                            {'ดูไฟล์'}
                          </a>
                        )
                      }
                      return '-'
                    },
                  },
                  {
                    title: 'ผล OCR',
                    dataIndex: 'result',
                    key: 'result',
                    width: 300,
                    render: (text) => {
                      if (text && text !== 'OCR result placeholder - waiting for OCR API URL') {
                        return <div style={{ maxWidth: '300px', wordBreak: 'break-word' }}>{text}</div>
                      }
                      return <span style={{ color: '#999' }}>รอผล OCR</span>
                    },
                  },
                  {
                    title: 'วันที่สร้าง',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    width: 180,
                    render: (text) => {
                      if (text) {
                        return new Date(text).toLocaleString('th-TH')
                      }
                      return '-'
                    },
                  },
                  {
                    title: 'วันที่อัพเดท',
                    dataIndex: 'updatedAt',
                    key: 'updatedAt',
                    width: 180,
                    render: (text) => {
                      if (text) {
                        return new Date(text).toLocaleString('th-TH')
                      }
                      return '-'
                    },
                  },
                  {
                    title: 'Export',
                    key: 'export',
                    width: 120,
                    fixed: 'right',
                    render: (text, record) => (
                      <Button
                        type="primary"
                        icon={<FileWordOutlined />}
                        onClick={() => handleExportToWord(record)}
                        size="small"
                      >
                        Word
                      </Button>
                    ),
                  },
                ]}
                dataSource={tableData}
                rowKey="id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  onChange: (page, pageSize) => {
                    setPagination((prev) => ({
                      ...prev,
                      current: page,
                      pageSize: pageSize,
                    }))
                    fetchTableData(page, pageSize, searchTerm)
                  },
                  onShowSizeChange: (current, size) => {
                    setPagination((prev) => ({
                      ...prev,
                      current: 1,
                      pageSize: size,
                    }))
                    fetchTableData(1, size, searchTerm)
                  },
                }}
                scroll={{ x: 'max-content' }}
                size="middle"
              />
            </Spin>
          </div>
        </div>
      )}
    />
  )
}

export default Airdoc
